import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminMemberEditorFields,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  createYowanAdminMemberWithAccount,
  ensureYowanAdminMemberMembership,
  getYowanAdminMutationErrorMessage,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const getMemberEnsureTargetFromError = (error, values = {}) => {
  const responseData = error?.response?.data || {};
  const existingAccount = responseData.existing_account || responseData.existingAccount || {};

  if (
    error?.response?.status === 409 &&
    responseData.recommended_action === 'ensure_membership' &&
    (existingAccount.auth_subject_id || existingAccount.authSubjectId)
  ) {
    return {
      authSubjectId: existingAccount.auth_subject_id || existingAccount.authSubjectId || '',
      loginEmail: existingAccount.login_email || existingAccount.loginEmail || values.email || '',
      hasMemberProfile: existingAccount.has_member_profile === true || existingAccount.hasMemberProfile === true,
      hasMemberAccess: existingAccount.has_member_access === true || existingAccount.hasMemberAccess === true,
      memberAccessIsActive:
        existingAccount.member_access_is_active === true || existingAccount.memberAccessIsActive === true,
      values: { ...values },
    };
  }

  return null;
};

const getMemberCreateErrorMessage = (error, content) => {
  const responseData = error?.response?.data || {};

  if (
    error?.response?.status === 409 &&
    ['duplicate_email', 'duplicate_subject'].includes(responseData.reason)
  ) {
    return content.form.duplicateEmailMessage;
  }

  return getYowanAdminMutationErrorMessage(error, content.form.errorMessage);
};

const AdminMembersCreatePage = () => {
  const content = yowanPageContent['/admin/members/new'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const navigate = useNavigate();
  const [submitMessage, setSubmitMessage] = useState('');
  const [ensureTarget, setEnsureTarget] = useState(null);
  const [ensureMessage, setEnsureMessage] = useState('');
  const [isEnsuringMembership, setIsEnsuringMembership] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: content?.form.defaults,
  });

  if (!content) {
    return null;
  }

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitMessage(content.form.pendingMessage);
    setEnsureTarget(null);
    setEnsureMessage('');

    try {
      await createYowanAdminMemberWithAccount(values);
      setSubmitMessage(content.form.successMessage);
      navigate(appendYowanPreviewQueryIfNeeded('/admin/members', preservedDomainSearch));
    } catch (error) {
      const nextEnsureTarget = getMemberEnsureTargetFromError(error, values);

      if (nextEnsureTarget) {
        setEnsureTarget(nextEnsureTarget);
        setSubmitMessage(content.form.duplicateEnsureMembershipMessage);
      } else {
        setSubmitMessage(getMemberCreateErrorMessage(error, content));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEnsureMembership = async () => {
    if (!ensureTarget?.authSubjectId) {
      return;
    }

    setIsEnsuringMembership(true);
    setEnsureMessage(content.form.ensureMembershipPendingMessage);

    try {
      const result = await ensureYowanAdminMemberMembership({
        ...ensureTarget.values,
        authSubjectId: ensureTarget.authSubjectId,
      });
      setEnsureMessage(content.form.ensureMembershipSuccessMessage);
      const memberHref = result.member?.id ? `/admin/members/${result.member.id}/edit` : '/admin/members';
      navigate(appendYowanPreviewQueryIfNeeded(memberHref, preservedDomainSearch));
    } catch (error) {
      setEnsureMessage(getYowanAdminMutationErrorMessage(error, content.form.ensureMembershipErrorMessage));
    } finally {
      setIsEnsuringMembership(false);
    }
  };

  return (
    <AdminBlogShell activeNavHref="/admin/members" routeKey="/admin/members/new">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanPreviewQueryIfNeeded('/admin/members', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
          <Link key="member-access" to={appendYowanPreviewQueryIfNeeded('/admin/access/member', preservedDomainSearch)}>
            会員アクセス一覧へ
          </Link>,
          <Link key="operator-access" to={appendYowanPreviewQueryIfNeeded('/admin/access/operator', preservedDomainSearch)}>
            管理権限一覧へ
          </Link>,
        ]}
      />

      <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
        <AdminMemberEditorFields content={content} register={register} errors={errors} />

        <div className="yowan-admin-form__actions">
          <button type="submit" className="c-form__submit" disabled={isSubmitting}>
            {isSubmitting ? content.form.pendingMessage : content.form.submitLabel}
          </button>
        </div>

        {submitMessage ? <p className="yowan-admin-form__message">{submitMessage}</p> : null}

        {ensureTarget ? (
          <section className="yowan-admin-detail-panel">
            <header className="yowan-admin-event-detail__section-header">
              <h2 className="yowan-admin-detail-panel__title">{content.form.ensureMembershipTitle}</h2>
              <p className="yowan-admin-detail-panel__lead">{content.form.ensureMembershipLead}</p>
            </header>
            <div className="yowan-admin-event-detail__grid">
              <label className="c-form__field yowan-admin-readonly-field">
                <span className="c-form__label">{content.form.ensureMembershipEmailLabel}</span>
                <input className="c-form__control" readOnly type="text" value={ensureTarget.loginEmail || '-'} />
              </label>
              <label className="c-form__field yowan-admin-readonly-field">
                <span className="c-form__label">{content.form.ensureMembershipProfileLabel}</span>
                <input
                  className="c-form__control"
                  readOnly
                  type="text"
                  value={ensureTarget.hasMemberProfile ? '作成済み' : '未作成'}
                />
              </label>
              <label className="c-form__field yowan-admin-readonly-field">
                <span className="c-form__label">{content.form.ensureMembershipAccessLabel}</span>
                <input
                  className="c-form__control"
                  readOnly
                  type="text"
                  value={
                    ensureTarget.hasMemberAccess
                      ? (ensureTarget.memberAccessIsActive ? '有効' : '停止中')
                      : '未付与'
                  }
                />
              </label>
            </div>
            <div className="yowan-admin-form__actions">
              <button
                type="button"
                className="c-form__submit"
                disabled={isEnsuringMembership}
                onClick={handleEnsureMembership}
              >
                {content.form.ensureMembershipActionLabel}
              </button>
            </div>
            {isEnsuringMembership ? (
              <p className="yowan-admin-form__message yowan-admin-form__message--pending">
                <LoadingSpinner inline size="sm" />
                <span>{ensureMessage}</span>
              </p>
            ) : ensureMessage ? (
              <p className="yowan-admin-form__message">{ensureMessage}</p>
            ) : null}
          </section>
        ) : null}
      </form>
    </AdminBlogShell>
  );
};

export default AdminMembersCreatePage;
